import React from 'react';
import {
  Ban,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  MapPin,
  User,
  X,
} from 'lucide-react';

import './CalendarioDeTurnos.css';

/**
 * Vista calendario de Gestión de Turnos: una columna por día, con el rango
 * elegido en la barra (4, 5 o 7 días).
 *
 * Antes era una semana fija: `getWeekDays()` anclaba el rango al domingo de la
 * semana en curso y el grid pedía siempre siete columnas, con la cantidad real
 * de columnas decidida por breakpoints (7 → 4 → 2 → 1). Eso tenía dos
 * problemas: la cantidad de días no era del negocio sino del ancho de la
 * ventana, y en el rango de tablet cada columna quedaba en ~200px, que no
 * alcanza para leer un turno.
 *
 * Ahora la ventana arranca en hoy, se elige a mano y se navega de a un rango
 * completo. El grid es un `auto-fit` con mínimo de 220px: con cuatro días da
 * cuatro columnas anchas, con siete envuelve en dos filas en vez de apretar, y
 * no hace falta ningún breakpoint para el ancho.
 *
 * Las clases van prefijadas `calendario-turnos__` a propósito. Las anteriores
 * (`day-header`, `day-name`, `day-number`, `calendar-header`) son nombres tan
 * genéricos que estaban definidos más de una vez en Dashboard.css y además
 * chocaban con BookingCalendar, BlockingCalendar y PricingCalendar, que usan
 * los mismos: el nombre del día del calendario lo terminaba pintando una regla
 * de las métricas de facturación (Dashboard.css:8346).
 */

/* La fecha se arma con los getters locales y no con `toISOString()`, que pasa
   por UTC: `appointment.date` es un YYYY-MM-DD local, así que convertir a UTC
   corre el día para cualquier zona al este de Greenwich. */
const claveFecha = (fecha) =>
  `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}-${String(
    fecha.getDate()
  ).padStart(2, '0')}`;

const armarDias = (cantidad, desplazamiento) => {
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const claveHoy = claveFecha(hoy);

  const inicio = new Date(hoy);
  inicio.setDate(hoy.getDate() + desplazamiento);

  return Array.from({ length: cantidad }, (_, i) => {
    const dia = new Date(inicio);
    dia.setDate(inicio.getDate() + i);
    const clave = claveFecha(dia);

    return {
      clave,
      nombre: dia.toLocaleDateString('es-AR', { weekday: 'short' }),
      numero: dia.getDate(),
      mes: dia.toLocaleDateString('es-AR', { month: 'short' }),
      esHoy: clave === claveHoy,
      esPasado: clave < claveHoy,
      fecha: dia,
    };
  });
};

const formatearRango = (dias) => {
  if (dias.length === 0) return '';
  const primero = dias[0];
  const ultimo = dias[dias.length - 1];
  const mismoMes = primero.mes === ultimo.mes;

  return mismoMes
    ? `${primero.numero} – ${ultimo.numero} de ${ultimo.mes}`
    : `${primero.numero} ${primero.mes} – ${ultimo.numero} ${ultimo.mes}`;
};

export default function CalendarioDeTurnos({
  dias = 7,
  desplazamiento = 0,
  onDesplazar,
  turnosDelDia,
  esPasado,
  mostrarPasados,
  resolverPersonal,
  resolverLugar,
  onCambiarEstado,
  onCancelar,
  onBorrar,
}) {
  const columnas = armarDias(dias, desplazamiento);

  return (
    <div className="calendario-turnos">
      <div className="calendario-turnos__encabezado">
        <div className="calendario-turnos__navegacion">
          <button
            type="button"
            onClick={() => onDesplazar(desplazamiento - dias)}
            aria-label={`Ver los ${dias} días anteriores`}
          >
            <ChevronLeft size={18} aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => onDesplazar(desplazamiento + dias)}
            aria-label={`Ver los ${dias} días siguientes`}
          >
            <ChevronRight size={18} aria-hidden="true" />
          </button>
        </div>

        <h3>{formatearRango(columnas)}</h3>

        {desplazamiento !== 0 && (
          <button
            type="button"
            className="calendario-turnos__hoy"
            onClick={() => onDesplazar(0)}
          >
            Volver a hoy
          </button>
        )}
      </div>

      <div className="calendario-turnos__grilla">
        {columnas.map((dia) => {
          const turnos = turnosDelDia(dia.clave);

          return (
            <div
              key={dia.clave}
              className={`calendario-turnos__dia ${dia.esHoy ? 'es-hoy' : ''} ${
                dia.esPasado ? 'es-pasado' : ''
              }`}
            >
              <div className="calendario-turnos__dia-titulo">
                <span className="calendario-turnos__dia-nombre">{dia.nombre}</span>
                <span className="calendario-turnos__dia-numero">{dia.numero}</span>
              </div>

              <div className="calendario-turnos__dia-cuerpo">
                {turnos.length === 0 ? (
                  /* Un día pasado sin turnos casi nunca está vacío de verdad:
                     el filtro de turnos pasados los está escondiendo, y decir
                     sólo "Sin turnos" hace pensar que el calendario se rompió
                     al navegar hacia atrás. */
                  <p className="calendario-turnos__vacio">
                    {dia.esPasado && !mostrarPasados
                      ? 'Turnos pasados ocultos'
                      : 'Sin turnos'}
                  </p>
                ) : (
                  turnos.map((turno) => {
                    const pasado = esPasado(turno);

                    return (
                      <article
                        key={turno.id}
                        className={`calendario-turnos__turno ${turno.status} ${
                          pasado ? 'es-pasado' : ''
                        }`}
                      >
                        {/* La misma cruz de borrado que la vista de lista, en
                            la misma esquina: las dos vistas muestran los mismos
                            turnos y tienen que poder hacer lo mismo con ellos.
                            La ✕ de abajo, entre las acciones, cancela; ésta
                            borra y no tiene vuelta atrás. */}
                        <button
                          type="button"
                          className="calendario-turnos__borrar"
                          onClick={() => onBorrar(turno)}
                          title="Borrar el turno definitivamente"
                          aria-label={`Borrar el turno de ${turno.customerName}`}
                        >
                          <X size={13} aria-hidden="true" />
                        </button>

                        <div className="calendario-turnos__hora">{turno.time}</div>
                        <h4>{turno.customerName}</h4>
                        <p>{turno.service}</p>

                        {/* EL TEXTO VA ENVUELTO, y no es cosmético: el <span>
                            de afuera es `display: flex` para alinear el ícono,
                            y `text-overflow: ellipsis` NO SE APLICA a un
                            contenedor flex. Con el texto suelto ahí adentro se
                            cortaba a lo bruto y sin puntos suspensivos —"María
                            Gonzál", "Cabina privac"—, que no se lee como un
                            nombre recortado sino como un dato roto. El
                            envoltorio es quien recorta; el `title` sigue
                            teniendo el nombre entero. */}
                        <div className="calendario-turnos__asignaciones">
                          <span title={resolverPersonal(turno)}>
                            <User size={12} aria-hidden="true" />
                            <span className="calendario-turnos__etiqueta">
                              {resolverPersonal(turno)}
                            </span>
                          </span>
                          <span title={resolverLugar(turno)}>
                            <MapPin size={12} aria-hidden="true" />
                            <span className="calendario-turnos__etiqueta">
                              {resolverLugar(turno)}
                            </span>
                          </span>
                        </div>

                        <div className="calendario-turnos__acciones">
                          <button
                            type="button"
                            className="es-confirmar"
                            onClick={() => onCambiarEstado(turno.id, 'confirmed')}
                            disabled={
                              turno.status === 'confirmed' || turno.status === 'cancelled'
                            }
                            title={
                              turno.status === 'cancelled'
                                ? 'No se puede confirmar un turno cancelado'
                                : 'Confirmar'
                            }
                          >
                            <Check size={14} aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className="es-pendiente"
                            onClick={() => onCambiarEstado(turno.id, 'pending')}
                            disabled={
                              turno.status === 'pending' || turno.status === 'cancelled'
                            }
                            title={
                              turno.status === 'cancelled'
                                ? 'No se puede cambiar un turno cancelado'
                                : 'Marcar como pendiente'
                            }
                          >
                            <Clock size={14} aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className="es-cancelar"
                            onClick={() => onCancelar(turno)}
                            disabled={turno.status === 'cancelled'}
                            title={
                              turno.status === 'cancelled'
                                ? 'Ya está cancelado'
                                : 'Cancelar (libera el horario)'
                            }
                          >
                            {turno.status === 'cancelled' ? (
                              <Ban size={14} aria-hidden="true" />
                            ) : (
                              <X size={14} aria-hidden="true" />
                            )}
                          </button>
                        </div>
                      </article>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
